import LoyaltyTierLabel from 'components/Common/Labels/LoyaltyTierLabel'
import LayoutContainer from 'components/Common/LayoutContainer'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import { useAppSelector } from 'hooks/reduxHooks'
import LoyaltyProgramNameLogo from 'loyalty/components/LoyaltyProgramNameLogo'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import useLoyaltyAccount from 'loyalty/hooks/useLoyaltyAccount'
import useLoyaltyTierProgressionMode from 'loyalty/hooks/useLoyaltyTierProgressionMode'
import { rem } from 'polished'
import { useCallback, useContext, useMemo } from 'react'
import * as React from 'react'
import type { IntlShape } from 'react-intl'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import Divider from 'LuxKit/Divider'
import { tierGradientBackgroundStyle, tierNormalContrastBackgroundStyle, tierNormalContrastColorStyle } from 'styles/utils/tierStyles'
import zIndex from 'styles/tools/z-index'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import useCurrentLoyaltyPendingPoints from 'loyalty/hooks/useCurrentLoyaltyPendingPoints'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Tooltip from 'LuxKit/Tooltip'
import CopyToClipboardClickableIcon from 'checkout/Components/Confirmation/BookingDetailsV2/CopyToClipboardClickableIcon'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import useLoyaltyConfig from 'loyalty/hooks/useLoyaltyConfig'
import LoyaltyAccountTileCircularProgressBar from 'loyalty/components/LoyaltyAccountTileCircularProgressBar'
import Caption from 'LuxKit/Typography/Caption'
import LoyaltyHeroUnlockTierCard from 'loyalty/components/LoyaltyHeroUnlockTierCard'
import useNavigateTo from 'hooks/useNavigateTo'
import { LOYALTY_LANDING_PAGE, LOYALTY_PURCHASE_POINTS_PAGE } from 'loyalty/constants/loyaltyRoutes'
import getIsLoyaltyPurchasePointsEnabled from 'loyalty/selectors/getIsLoyaltyPurchasePointsEnabled'
import useLoyaltyPurchasePointsOffers from 'loyalty/hooks/useLoyaltyPurchasePointsOffers'
import useCurrentLoyaltyActivities from 'loyalty/hooks/useCurrentLoyaltyActivities'
import ModalContext from 'contexts/ModalContext'
import LoyaltyPointsDeductionInfoModal from 'loyalty/components/LoyaltyPointsDeductionInfoModal'
import LoyaltyMembershipYearModal from 'loyalty/components/LoyaltyMembershipYearModal'
import useCurrentMembershipYear from 'loyalty/hooks/useCurrentMembershipYear'
import { LOYALTY_ACCRUAL_DEDUCTION_CUT_OFF_DATE, LOYALTY_DISMISS_POINTS_DEDUCTION_MODAL_STORAGE_KEY } from 'loyalty/constants/loyaltyConfig'
import { dateIsAfterOrSame, dateIsBefore } from 'lib/datetime/dateUtils'
import { middleDotSeparatorMessage } from 'LuxKit/intl/separators'
import { get as getLocalStorage } from 'lib/storage/isomorphicLocalStorage'
import { LUX_LOYALTY_POINTS_DEDUCTION_DATE } from '@luxuryescapes/lib-lux-loyalty'
import useOrders from 'hooks/Order/useOrders'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import getTasteOfGoldPromoCampaign from 'takeovers/tasteOfGoldTakeover/selectors/getTasteOfGoldPromoCampaign'
import { TasteOfGoldPromoCard } from 'takeovers/tasteOfGoldTakeover/components/TasteOfGoldPromoStyles'
import TasteOfGoldPromoContent from 'takeovers/tasteOfGoldTakeover/components/TasteOfGoldPromoContent'

function getTimeBasedGreeting(intl: IntlShape): string {
  const hour = new Date().getHours()
  if (hour < 12) return intl.formatMessage({ id: 'ZbF0uWz63f6lExSbfzY5', defaultMessage: 'Good morning', description: 'Time-based greeting for morning hours on the promotional landing page' })
  if (hour < 18) return intl.formatMessage({ id: 'nHtQQILwxiUcDEVph28i', defaultMessage: 'Good afternoon', description: 'Time-based greeting for afternoon hours on the promotional landing page' })
  return intl.formatMessage({ id: '08Df2o1Sjgy5HQj1DK78', defaultMessage: 'Good evening', description: 'Time-based greeting for evening hours on the promotional landing page' })
}

const HeroSection = styled.div`
  position: relative;
  padding: ${rem(32)} ${rem(16)};
  overflow: visible;
  z-index: ${zIndex.loyaltyAccountHero};
  ${tierGradientBackgroundStyle}
  ${tierNormalContrastColorStyle}

  /* Custom 450 breakpoint to differentiate between mobile and tablet */
  @media (min-width: 450px) {
    padding: ${rem(40)} 0;
  }
`

const TierDivider = styled(Divider)`
  && {
    margin-top: auto;
    margin-bottom: auto;
    height: ${rem(48)};
    opacity: 0.5;

    @media (min-width: 450px) {
      height: ${rem(80)};
    }
  }
  ${tierNormalContrastBackgroundStyle}
`

const HeroContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${rem(16)};
  text-align: start;

  @media (min-width: 450px) {
    align-items: center;
  }

  ${mediaQueryUp.desktop} {
    display: grid;
    justify-content: stretch;
    align-items: start;
    gap: ${rem(24)};
    grid-template-columns: 1fr minmax(${rem(280)}, ${rem(383)});
  }

  ${mediaQueryUp.largeDesktop} {
    gap: ${rem(40)};
  }
`

const TierCardWrapper = styled.div`
  width: 100%;
  text-align: start;
  align-self: flex-start;
  margin-top: ${rem(12)};

  @media (min-width: 450px) {
    width: auto;
    max-width: ${rem(400)};
    margin-top: ${rem(16)};
    align-self: center;
  }

  ${mediaQueryUp.desktop} {
    width: 100%;
    max-width: none;
    min-width: 0;
    padding-top: ${rem(37)};
    margin-top: 0;
    align-self: auto;
  }
`

const LinkWrapper = styled.div`
  min-height: ${rem(24)};
`

const MembershipInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  @media (min-width: 450px) {
    flex-direction: row;
    flex-wrap: wrap;
  }
`

const MembershipDot = styled(BodyText)`
  display: none;
  padding: 0 ${rem(8)};

  @media (min-width: 450px) {
    display: block;
  }
`

/* Progress bar item - vertical on mobile, horizontal on tablet+ */
const ProgressBarItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: ${rem(8)};

  @media (min-width: 450px) {
    flex-direction: row;
    gap: ${rem(10)};
    text-align: start;
  }
`

/* Left section wrapper */
const LeftSectionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(15)};
  width: 100%;

  @media (min-width: 450px) {
    gap: ${rem(20)};
    width: auto;
  }
`

const TopInfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(6)};

  @media (min-width: 450px) {
    gap: ${rem(8)};
  }
`

const InfoIconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  line-height: 1;
`

const PointsSection = styled.div`
  padding: ${rem(8)} 0 0 0;

  @media (min-width: 450px) {
    padding: ${rem(8)} 0;
  }
`

const ProgressCirclesWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  width: 100%;
  padding-inline-start: ${rem(30)};
  padding-top: ${rem(8)};

  @media (min-width: 450px) {
    padding-inline-start: 0;
    padding-top: 0;
  }
`

/* Container for the two progress bars */
const ProgressBarsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: ${rem(95)};

  @media (min-width: 450px) {
    gap: ${rem(55)};
  }
`

const FadedLoadingBox = styled(LoadingBox)`
  opacity: 0.3;
`

const FadedTextLoadingBox = styled(TextLoadingBox)`
  opacity: 0.3;
`

const DesktopPromoWrapper = styled.div`
  margin-top: ${rem(16)};
`

const TabletPromoCardWrapper = styled.div`
  width: 100%;
  align-self: flex-start;

  @media (min-width: 450px) {
    width: auto;
    max-width: ${rem(400)};
    align-self: center;
  }

  ${mediaQueryUp.tablet} {
    max-width: ${rem(383)};
  }
`

interface Props {
  activeTab: string
}

function LoyaltyAccountLandingPageHero(props: Props) {
  const intl = useIntl()
  const { activeTab } = props
  const firstName = useAppSelector(state => state.auth.account.givenName)
  const account = useLoyaltyAccount().data!
  const membershipYear = useCurrentMembershipYear()
  const [tier, tierName, _nextTier, nextTierName] = useCurrentLoyaltyTier()
  const membershipYearEnd = membershipYear?.end.toISOString()
  const retainVariant = membershipYear?.retainVariant ?? 'current-year'
  const {
    unlockCardTier,
    effectiveRetainMode,
    canToggleView,
    showUpgradeView,
    setShowUpgradeView,
    targetCredits,
    targetNights,
    creditsRemaining,
    nightsRemaining,
    hasRetainedTier,
    statusCreditsProgress,
    nightsProgress,
  } = useLoyaltyTierProgressionMode()
  const [pendingPoints, pendingPointsFetching] = useCurrentLoyaltyPendingPoints()
  const [activities, activitiesFetching, , ,] = useCurrentLoyaltyActivities()
  const [userOrders, hasFetchedUserOrders] = useOrders()
  const navigateTo = useNavigateTo()
  const hasDismissedPointsDeductionModal = !!getLocalStorage(LOYALTY_DISMISS_POINTS_DEDUCTION_MODAL_STORAGE_KEY)
  const showModal = useContext(ModalContext)
  const hasLoyaltyPointsDeductionWarningEnabled = useAppSelector(state => !!state.optimizely.optimizelyFeatureFlags[OptimizelyFeatureFlags.loyaltyPointsDeductionWarning])
  const isTasteOfGoldActive = !!useAppSelector(getTasteOfGoldPromoCampaign)
  const isLoyaltyPurchasePointsEnabled = useAppSelector(getIsLoyaltyPurchasePointsEnabled)
  const { customerHasEligibleOffers } = useLoyaltyPurchasePointsOffers()

  const hasManualPointsDeduction = useMemo<boolean>(() => {
    return activities.some(activity => activity.type === 'points_ratio_adjust')
  }, [activities])

  const hasPendingPointsDeduction = useMemo<boolean>(() => {
    return pendingPoints.items.some(item => dateIsBefore(new Date(item.createdAt), new Date(LUX_LOYALTY_POINTS_DEDUCTION_DATE)))
  }, [pendingPoints.items])

  const hasAccrualPointsDeduction = useMemo<boolean>(() => {
    return activities.some(activity => {
      if (activity.type !== 'earn_points') return false
      // Accrual happened after 27th June 2025 AEST
      if (!dateIsAfterOrSame(new Date(activity.date), new Date(LOYALTY_ACCRUAL_DEDUCTION_CUT_OFF_DATE))) return false
      const activityOrder = userOrders.find(order => order.id === activity.orderId)
      // Booking date is before the points deduction date
      return activityOrder && dateIsBefore(new Date(activityOrder.createdAt), new Date(LUX_LOYALTY_POINTS_DEDUCTION_DATE))
    })
  }, [activities, userOrders])

  const hasPointsDeduction =
    hasManualPointsDeduction ||
    (hasPendingPointsDeduction && hasLoyaltyPointsDeductionWarningEnabled) ||
    (hasAccrualPointsDeduction && hasLoyaltyPointsDeductionWarningEnabled)

  const handlePointsDeductionModal = useCallback(() => (
    showModal(<LoyaltyPointsDeductionInfoModal />)
  ), [showModal])

  const handleMembershipYearModal = useCallback(() => {
    if (account) {
      showModal(<LoyaltyMembershipYearModal account={account} />)
    }
  }, [showModal, account])

  const isFetching = activitiesFetching || pendingPointsFetching || !hasFetchedUserOrders

  const handleViewActivityClick = useCallback((event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    event.preventDefault()
    document.getElementById('loyalty-your-activity')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  // Handle view activity from 'about' tab - switches to benefits tab and scrolls to activity section
  const handleViewActivityFromAbout = useCallback((event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    event.preventDefault()
    navigateTo(`/${LOYALTY_LANDING_PAGE}#benefits`)
    setTimeout(() => {
      document.getElementById('loyalty-your-activity')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 100)
  }, [navigateTo])

  const greetingLabel = firstName
    ? intl.formatMessage(
        {
          id: 'bPDeQctlPUJ9ihFL9O78',
          defaultMessage: '{greeting}, {firstName}',
          description: 'Greeting with member first name on loyalty account page',
        },
        { greeting: getTimeBasedGreeting(intl), firstName },
      )
    : getTimeBasedGreeting(intl)

  const membershipNumber = `${account.accountNumber}`

  // Ensure loyalty config (tier thresholds) is fetched and available in Redux state
  useLoyaltyConfig()

  const handleViewDetails = useCallback(() => {
    // Platinum tier anchors to "Your Platinum benefits" section, other tiers anchor to "Unlock [next tier]" section
    const anchor = effectiveRetainMode ? 'benefits-your-tier' : 'benefits-your-benefits'
    const element = document.getElementById(anchor)

    if (element) {
      // Already on benefits tab - scroll immediately
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    } else {
      // If need to switch tabs - navigate and let useEffect in target component handle scroll
      navigateTo(`/${LOYALTY_LANDING_PAGE}#${anchor}`)
    }
  }, [navigateTo, effectiveRetainMode])

  return <ProductPaletteProvider palette={`tier-${tier}`}>
    <HeroSection className={`tier-${tier}`}>
      <LayoutContainer variant="medium">
        <HeroContent>
          <LeftSectionContent>
            <TopInfoSection>
              <Group
                direction="horizontal"
                gap={12}
                horizontalAlign="start"
                verticalAlign="center"
              >
                <LoyaltyProgramNameLogo width={104} />
                <LoyaltyTierLabel kind="primary" tier={tier} format="uppercase">{tierName}</LoyaltyTierLabel>
              </Group>

              {/* Greeting based on local time */}
              <Heading variant="heading2">
                {greetingLabel}
              </Heading>

              <MembershipInfo>
                <Group direction="horizontal" gap={4} verticalAlign="center">
                  <BodyText variant="medium">
                    {intl.formatMessage({ id: 'ksULOdf9OVP8fufigbjl', defaultMessage: 'Membership {membershipNumber}', description: 'Label showing user membership number on the promotional landing page' }, { membershipNumber })}
                  </BodyText>
                  <CopyToClipboardClickableIcon
                    value={membershipNumber}
                    ariaLabel={intl.formatMessage({ id: 'gxjYRARw_zM4ZUg47e33', defaultMessage: 'Copy membership number', description: 'Aria label for copy membership number button for the membership' })}
                  />
                </Group>
                {account.statusReviewDate && (
                  <>
                    <MembershipDot variant="medium">{intl.formatMessage(middleDotSeparatorMessage)}</MembershipDot>
                    <BodyText variant="medium">
                      <FormattedMessage
                        id="nBj6jejzIBe87JXsu2L2"
                        defaultMessage="Tier review date {date}"
                        description="Membership status tier review date label with date value"
                        values={{ date: <FormatDateTime value={account.statusReviewDate} format="dmy-casual-short" /> }}
                      />
                    </BodyText>
                  </>
                )}
              </MembershipInfo>
            </TopInfoSection>

            {/* Points summary */}
            <PointsSection>
              {isFetching && (
                <Group direction="horizontal" gap={8} verticalAlign="center">
                  <FadedTextLoadingBox width="6ch" typography="heading3" />
                  <FadedTextLoadingBox width="6ch" typography="body-medium" />
                </Group>
              )}
              {!isFetching && (
                <Group direction="vertical" gap={4}>
                  <Group direction="horizontal" gap={16} verticalAlign="center" wrap="wrap">
                    <Group direction="horizontal" gap={4} verticalAlign="center">
                      <Heading variant="heading3">
                        <FormatDecimal value={account.points} />
                      </Heading>
                      <BodyText variant="medium" weight="bold">
                        {intl.formatMessage({ id: '1ctyQqUlhONyImYNSBMd', defaultMessage: 'points', description: 'Label for the loyalty points count in the tier progression tracker on the account page' })}
                      </BodyText>
                    </Group>
                    <Group direction="horizontal" gap={4} verticalAlign="center">
                      <BodyText variant="medium">
                        <FormattedMessage
                          id="E5yjZp88aViZnM9Sfuqo"
                          defaultMessage="{count} pending"
                          description="Count of pending loyalty points that have not yet been credited to the account"
                          values={{ count: <FormatDecimal value={pendingPoints.points} /> }}
                        />
                      </BodyText>
                      <Tooltip description={intl.formatMessage({ id: 'MNjBpmX8tq42ZH7nJ7fk', defaultMessage: 'Pending points may take up to 24hrs to appear', description: 'Tooltip explaining pending points delay in the loyalty program section' })} placement="bottom">
                        <InfoIconWrapper><LineInfoCircleIcon size={16} /></InfoIconWrapper>
                      </Tooltip>
                    </Group>
                    {isLoyaltyPurchasePointsEnabled && customerHasEligibleOffers && (
                      <TextLink size="medium" underline to={`/${LOYALTY_PURCHASE_POINTS_PAGE}`}>
                        {intl.formatMessage({
                          id: 'p9HkS3te2WFLfqils80t',
                          defaultMessage: 'Top up points',
                          description: 'Inline link in the Société account hero next to the points balance, takes the member to the Top up points page',
                        })}
                      </TextLink>
                    )}
                  </Group>
                  {hasPointsDeduction && !hasDismissedPointsDeductionModal && (
                    <TextLink
                      size="small"
                      weight="regular"
                      underline
                      onClick={handlePointsDeductionModal}
                    >
                      {intl.formatMessage({ id: 'z8Dww3rSxO4zTiuX3CDv', defaultMessage: 'What’s changed', description: 'Link text to view what has changed with points deduction' })}
                    </TextLink>
                  )}
                </Group>
              )}
            </PointsSection>

            <ProgressCirclesWrapper>
              {isFetching && (
                <ProgressBarsContainer>
                  <ProgressBarItem>
                    <FadedLoadingBox width={76} height={76} />
                    <div>
                      <FadedTextLoadingBox width="12ch" typography="body-small" />
                      <FadedTextLoadingBox width="8ch" typography="body-small" />
                    </div>
                  </ProgressBarItem>
                  <ProgressBarItem>
                    <FadedLoadingBox width={76} height={76} />
                    <div>
                      <FadedTextLoadingBox width="6ch" typography="body-small" />
                      <FadedTextLoadingBox width="8ch" typography="body-small" />
                    </div>
                  </ProgressBarItem>
                </ProgressBarsContainer>
              )}
              {!isFetching && (
                <ProgressBarsContainer>
                  <ProgressBarItem>
                    <LoyaltyAccountTileCircularProgressBar
                      progress={statusCreditsProgress}
                      tier={tier}
                      variant="onTierBackground"
                    >
                      <Group direction="vertical" horizontalAlign="center" gap={2}>
                        <BodyText variant="medium" weight="bold">
                          <FormatDecimal value={account.statusCredits} />
                        </BodyText>
                        <TierDivider className={`tier-${tier}`} kind="primary" />
                        <Caption variant="small">
                          <FormatDecimal value={targetCredits} />
                        </Caption>
                      </Group>
                    </LoyaltyAccountTileCircularProgressBar>

                    <Group direction="vertical" gap={4}>
                      <BodyText variant="small" weight="bold">{intl.formatMessage({ id: 'rMCJgGfX12UHORaOtYE9', defaultMessage: 'Status Credits', description: 'Label for the status credits count in the loyalty tier progression tracker display' })}</BodyText>
                      <Group direction="horizontal" gap={4} verticalAlign="center">
                        <BodyText variant="small">
                          <FormattedMessage
                            id="4lLwC9339Dwp-nF6zV_e"
                            defaultMessage="{count} pending"
                            description="Count of pending status credits that have not yet been credited to the loyalty account"
                            values={{ count: <FormatDecimal value={pendingPoints.statusCredits} /> }}
                          />
                        </BodyText>
                        <Tooltip description={intl.formatMessage({ id: 'Osp2EL95u1nIPTnfOlHI', defaultMessage: 'Pending status credits may take up to 24hrs to appear', description: 'Tooltip explaining pending status credits delay on the promotional landing page' })} placement="bottom">
                          <InfoIconWrapper><LineInfoCircleIcon size={16} /></InfoIconWrapper>
                        </Tooltip>
                      </Group>
                    </Group>
                  </ProgressBarItem>

                  <ProgressBarItem>
                    <LoyaltyAccountTileCircularProgressBar
                      progress={nightsProgress}
                      tier={tier}
                      variant="onTierBackground"
                    >
                      <Group direction="vertical" horizontalAlign="center" gap={2}>
                        <BodyText variant="medium" weight="bold">
                          <FormatDecimal value={account.numberOfNights} />
                        </BodyText>
                        <TierDivider className={`tier-${tier}`} kind="primary" />
                        <Caption variant="small">
                          <FormatDecimal value={targetNights} />
                        </Caption>
                      </Group>
                    </LoyaltyAccountTileCircularProgressBar>

                    <Group direction="vertical" gap={4}>
                      <BodyText variant="small" weight="bold">{intl.formatMessage({ id: 'g3OKdS1bAjzo0CHq-YTF', defaultMessage: 'Nights', description: 'Label for the loyalty nights count in the tier progression tracker on the account page' })}</BodyText>
                      <Group direction="horizontal" gap={4} verticalAlign="center">
                        <BodyText variant="small">
                          <FormattedMessage
                            id="R-geaU20IOyqujSR7eeU"
                            defaultMessage="{count} pending"
                            description="Count of pending loyalty nights that have not yet been credited to the account"
                            values={{ count: <FormatDecimal value={pendingPoints.items.reduce((acc, item) => acc + (item.numNights ?? 0), 0)} /> }}
                          />
                        </BodyText>
                        <Tooltip description={intl.formatMessage({ id: 'jnkmclgcBWVxvPYElEmh', defaultMessage: 'Pending nights may take up to 24hrs to appear', description: 'Tooltip explaining pending nights delay on the promotional landing page' })} placement="bottom">
                          <InfoIconWrapper><LineInfoCircleIcon size={16} /></InfoIconWrapper>
                        </Tooltip>
                      </Group>
                    </Group>
                  </ProgressBarItem>
                </ProgressBarsContainer>
              )}
            </ProgressCirclesWrapper>

            {membershipYearEnd && <Group direction="vertical" gap={2}>
              <BodyText variant="small">
                <FormattedMessage
                  id="0FK4p45Hk2jPsLZINVw0"
                  defaultMessage="Your Status Credits & Nights reset on {date}."
                  description="Status credits and nights reset date shown below progress circles on loyalty landing page"
                  values={{
                    date: <FormatDateTime value={membershipYearEnd} format="day-long-month-long-year" />,
                  }}
                />
              </BodyText>
              <TextLink size="small" onClick={handleMembershipYearModal}>
                {intl.formatMessage({
                  id: '0qw5T4GPSJAT2ZfBmE09',
                  defaultMessage: 'How membership years work',
                  description: 'Link text to open membership year explanation modal on loyalty landing page',
                })}
              </TextLink>
            </Group>}

            <LinkWrapper>
              {activeTab === 'about' && <TextLink
                size="medium"
                block
                href="#benefits"
                onClick={handleViewActivityFromAbout}
              >
                {intl.formatMessage({ id: 'PYAkCi4M4zNdGWB9HEq9', defaultMessage: 'View activity', description: 'Link to view loyalty activity from about tab in the loyalty section' })}
              </TextLink>}
              {activeTab === 'benefits' && <TextLink
                size="medium"
                block
                href="#loyalty-your-activity"
                onClick={handleViewActivityClick}
              >
                {intl.formatMessage({ id: 'fzLOHUNj0Z_LtVjEyNfk', defaultMessage: 'View activity', description: 'Link to view loyalty activity from benefits tab on the promotional landing page' })}
              </TextLink>}
            </LinkWrapper>
          </LeftSectionContent>
          {isTasteOfGoldActive && <CSSBreakpoint max="tablet"><TabletPromoCardWrapper><TasteOfGoldPromoCard><TasteOfGoldPromoContent layout="vertical" size="small" /></TasteOfGoldPromoCard></TabletPromoCardWrapper></CSSBreakpoint>}
          {/* Right Section - tier card */}
          {unlockCardTier && (
            <TierCardWrapper>
              <LoyaltyHeroUnlockTierCard
                tier={unlockCardTier}
                isRetain={effectiveRetainMode}
                retainVariant={retainVariant}
                hasRetainedTier={hasRetainedTier}
                creditsRemaining={creditsRemaining}
                nightsRemaining={nightsRemaining}
                tierReviewDate={account.statusReviewDate}
                membershipYearEndDate={membershipYearEnd}
                onViewDetails={handleViewDetails}
                showToggle={canToggleView}
                showUpgradeView={showUpgradeView}
                onToggleView={setShowUpgradeView}
                currentTierName={tierName}
                nextTierName={nextTierName}
              />
            </TierCardWrapper>
          )}
        </HeroContent>
        {isTasteOfGoldActive && <CSSBreakpoint min="desktop"><DesktopPromoWrapper><TasteOfGoldPromoCard><TasteOfGoldPromoContent layout="horizontal" size="medium" /></TasteOfGoldPromoCard></DesktopPromoWrapper></CSSBreakpoint>}
      </LayoutContainer>

    </HeroSection>
  </ProductPaletteProvider>
}

export default LoyaltyAccountLandingPageHero
